/**
 * Business Perspective Selector
 *
 * Three-card selector for choosing the business perspective:
 * - R&D / Research: ML model development, predictive analytics, fairness analysis
 * - Quality / GxP: Data integrity, ALCOA+, process compliance, batch release
 * - Commercial Analytics: Marketing attribution, ROI analysis, HCP targeting
 *
 * Default: R&D (backward compatible).
 */

import React from 'react';
import './contextual-validity.css';

export type BusinessPerspective = 'rnd' | 'quality' | 'commercial';

interface PerspectiveSelectorProps {
  selected: BusinessPerspective | null;
  onSelect: (perspective: BusinessPerspective) => void;
  disabled?: boolean;
}

interface PerspectiveCard {
  id: BusinessPerspective;
  title: string;
  subtitle: string;
  description: string;
  bullets: string[];
  icon: string;
}

const PERSPECTIVES: PerspectiveCard[] = [
  {
    id: 'rnd',
    title: 'R&D / Research',
    subtitle: 'ML & Predictive Analytics',
    description: 'Assess data for ML model development with fairness and proxy discrimination analysis.',
    bullets: [
      'Classification, regression, clustering problems',
      'Fairness analysis & protected attribute detection',
      'Label quality & population coverage',
    ],
    icon: '\u{1F52C}', // Microscope
  },
  {
    id: 'quality',
    title: 'Quality / GxP',
    subtitle: 'Data Integrity & Compliance',
    description: 'Evaluate data against pharmaceutical quality and GxP data integrity standards.',
    bullets: [
      'ALCOA+ compliance assessment',
      'Process validation & deviation analysis',
      'Audit trail completeness & traceability',
    ],
    icon: '\u{1F3ED}', // Factory
  },
  {
    id: 'commercial',
    title: 'Commercial Analytics',
    subtitle: 'Marketing & Sales',
    description: 'Assess multi-source commercial data for attribution, ROI, and market analysis.',
    bullets: [
      'Source system consistency & reconciliation',
      'Attribution methodology soundness',
      'Coverage gaps & temporal alignment',
    ],
    icon: '\u{1F4CA}', // Bar chart
  },
];

export const PerspectiveSelector: React.FC<PerspectiveSelectorProps> = ({
  selected,
  onSelect,
  disabled = false,
}) => {
  return (
    <div className="perspective-selector">
      <div className="perspective-selector-header">
        <h3>Select Analysis Perspective</h3>
        <p className="perspective-selector-subtitle">
          Choose the perspective that matches your data assessment needs. This determines the analysis dimensions and form fields.
        </p>
      </div>
      <div className="perspective-cards">
        {PERSPECTIVES.map((card) => (
          <button
            key={card.id}
            type="button"
            className={`perspective-card ${selected === card.id ? 'selected' : ''}`}
            onClick={() => !disabled && onSelect(card.id)}
            disabled={disabled}
          >
            <div className="perspective-card-icon">{card.icon}</div>
            <div className="perspective-card-content">
              <h4 className="perspective-card-title">{card.title}</h4>
              <span className="perspective-card-subtitle">{card.subtitle}</span>
              <p className="perspective-card-description">{card.description}</p>
              <ul className="perspective-card-bullets">
                {card.bullets.map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                ))}
              </ul>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default PerspectiveSelector;
